import React, { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  EmailBanner as EmailBannerBase,
  buildEsuValidationSchema
} from '@comicrelief/component-library';
import axios from 'axios';
import { getImage } from 'gatsby-plugin-image';
import { paddingOptionMapping, donateOrientationMapping } from '../../utils/optionMapping';
import getCarriedOverEmail from '../../utils/getCarriedOverEmail';
import renderRichText from '../RichText/RichText';
import { changeTextColor } from '../RichText/utils';
import schemaOverride from './config';
import imagePropType, { videoPropType } from '../../commonPropTypes/commonPropTypes';

const processImage = (imageField, imageData) => {
  if (!imageData) return null;
  return {
    imageLow: imageData?.placeholder?.fallback,
    images: imageData?.images?.sources[0]?.srcSet,
    image: imageData?.images?.fallback?.src,
    alt: imageField?.description || ''
  };
};

const EmailBanner = ({ data }) => {
  const {
    widgetType,
    orientation,
    paddingAbove,
    paddingBelow,
    pageBackgroundColour,
    componentBackgroundColourTextVariantOnly,
    title,
    bodyCopy,
    privacyCopy,
    successCopy,
    ctaText,
    campaignCode,
    databaseTag,
    loopAmbientVideo,
    lVideo,
    smVideo,
    lImage,
    mImage,
    sImage
  } = data;

  const transSource = databaseTag || campaignCode || 'default';

  const cmsOrientation = orientation
    ? donateOrientationMapping[orientation]
    : undefined;

  const mappedPaddingAbove = paddingAbove ? paddingOptionMapping[paddingAbove] : undefined;
  const mappedPaddingBelow = paddingBelow ? paddingOptionMapping[paddingBelow] : undefined;

  const pageBgColour = pageBackgroundColour ? snakeCase(pageBackgroundColour) : undefined;
  const componentBgColour = widgetType && componentBackgroundColourTextVariantOnly
    ? snakeCase(componentBackgroundColourTextVariantOnly) : 'white';

  const lImageData = getImage(lImage);
  const mImageData = getImage(mImage);

  const imageL = processImage(lImage, lImageData);
  const imageM = processImage(mImage, mImageData);
  const imageS = processImage(sImage, getImage(sImage));

  const mediumPosterData = mImage?.posterImageData;
  const largePosterData = lImage?.posterImageData;

  const mediumPosterUrl = mediumPosterData?.images?.fallback?.src
    ?? mImageData?.images?.fallback?.src ?? mImage?.file?.url ?? null;
  const largePosterUrl = largePosterData?.images?.fallback?.src
    ?? lImageData?.images?.fallback?.src ?? lImage?.file?.url ?? null;

  const videoDesktop = lVideo?.file?.url;
  const videoMobile = smVideo?.file?.url;
  const videoLoopProp = loopAmbientVideo ?? true;

  const renderedBodyCopy = bodyCopy
    ? renderRichText(bodyCopy, componentBgColour, null, 'black') : null;
  const renderedPrivacyCopy = privacyCopy ? renderRichText(privacyCopy) : null;
  const renderedSuccessCopy = successCopy ? renderRichText(successCopy) : null;

  const carriedOverEmail = useMemo(() => getCarriedOverEmail(), []);

  const validationSchema = buildEsuValidationSchema(schemaOverride);
  const formMethods = useForm({
    mode: 'onBlur',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: carriedOverEmail || '',
      firstName: '',
      lastName: ''
    }
  });
  const { setError } = formMethods;

  const handleSubscribe = useCallback(async (formData) => {
    const payload = {
      ...formData,
      transSource,
      campaign: campaignCode,
      transSourceURL: window.location.href
    };

    if (!window.dataLayer) window.dataLayer = [];

    try {
      await axios.post(
        `${process.env.GATSBY_CONTACT_SERVICE_BASE_URL}/email-signup`,
        payload
      );

      window.dataLayer.push({ event: 'cr_esu_signup_body' });
    } catch ({ response: { data: errorData } }) {
      const errors = errorData.validation_errors;
      if (errors !== undefined) {
        const error = Object.values(errors)[0];
        setError('formError', { message: error });
      } else {
        setError('formError', {
          message: 'Sorry, there was a problem processing your subscription.'
        });
      }
    }
  }, [transSource, campaignCode, setError]);

  return (
    <EmailBannerBase
      emailWidgetIsTextOnly={widgetType || false}
      orientation={cmsOrientation}
      paddingAbove={mappedPaddingAbove}
      paddingBelow={mappedPaddingBelow}
      pageBackgroundColour={pageBgColour}
      componentBackgroundColour={componentBgColour}
      copyColor={widgetType ? changeTextColor(componentBgColour) : undefined}
      title={title}
      bodyCopy={renderedBodyCopy}
      privacyCopy={renderedPrivacyCopy}
      successCopy={renderedSuccessCopy}
      ctaText={ctaText}
      imageL={imageL}
      imageM={imageM}
      imageS={imageS}
      videoDesktop={videoDesktop}
      videoMobile={videoMobile}
      posterDesktop={largePosterUrl}
      posterMobile={mediumPosterUrl}
      videoLoop={videoLoopProp}
      formContext={formMethods}
      onSubmit={handleSubscribe}
    />
  );
};

EmailBanner.propTypes = {
  data: PropTypes.shape({
    widgetType: PropTypes.bool,
    orientation: PropTypes.string,
    paddingAbove: PropTypes.string,
    paddingBelow: PropTypes.string,
    pageBackgroundColour: PropTypes.string,
    componentBackgroundColourTextVariantOnly: PropTypes.string,
    title: PropTypes.string,
    bodyCopy: PropTypes.shape({
      raw: PropTypes.string
    }),
    privacyCopy: PropTypes.shape({
      raw: PropTypes.string
    }),
    successCopy: PropTypes.shape({
      raw: PropTypes.string
    }),
    ctaText: PropTypes.string,
    campaignCode: PropTypes.string,
    loopAmbientVideo: PropTypes.bool,
    lVideo: videoPropType,
    smVideo: videoPropType,
    databaseTag: PropTypes.string,
    lImage: imagePropType,
    mImage: imagePropType,
    sImage: imagePropType
  }).isRequired
};

export default EmailBanner;
